import {
	Box,
	Button,
	FormControl,
	FormControlLabel,
	Grid,
	Link,
	Modal,
	Paper,
	Radio,
	RadioGroup,
	Typography,
} from "@mui/material"
import React, {useState} from "react"
import {styled} from "@mui/material/styles"

import {api} from "@indebted/api"
import {EmailStatusIcon} from "@indebted/components/EmailStatusIcon"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type PickerEmail = {
	Value: string
	Status: {Icon: string; Description: string}
	ESIGNEligible: boolean
	RemovedAt?: string
}

function esignRecipientPickerEnabled(): boolean {
	return import.meta.env.VITE_ENABLE_ESIGN_RECIPIENT_PICKER === "true"
}

function ESIGNConsentRequestModal({
	customerID,
	emails,
	reload,
	linkText,
}: {
	customerID: string
	emails: PickerEmail[]
	reload: () => void
	linkText: string
}) {
	const [open, setOpen] = useState(false)
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const pickerEnabled = esignRecipientPickerEnabled()

	const listedEmails = emails.filter((email) => !email.RemovedAt)
	const eligibleEmails = listedEmails.filter((email) => email.ESIGNEligible)
	const [selectedEmail, setSelectedEmail] = useState(eligibleEmails.length === 1 ? eligibleEmails[0].Value : "")

	const requestESIGNConsent = (to?: string) => {
		api.customer
			.requestESIGNConsent(customerID, agentEmail, to)
			.then(() => {
				notification.success(`E-Sign consent request email sent.`)
				setTimeout(reload, 1000)
			})
			.catch((error) => {
				notification.error(error.Message)
			})
		setOpen(false)
	}

	return (
		<>
			<Link component="button" variant="body2" onClick={() => setOpen(true)}>
				{linkText}
			</Link>
			<Modal
				open={open ?? false}
				onClose={() => setOpen(false)}
				aria-labelledby="modal-title"
				aria-describedby="modal-description"
			>
				<StyledPaper>
					{pickerEnabled ? (
						<RecipientPicker
							listedEmails={listedEmails}
							hasEligibleEmail={eligibleEmails.length > 0}
							selectedEmail={selectedEmail}
							onSelect={setSelectedEmail}
							onSubmit={() => requestESIGNConsent(selectedEmail)}
							onCancel={() => setOpen(false)}
						/>
					) : (
						<LegacyConfirmation onConfirm={() => requestESIGNConsent()} onCancel={() => setOpen(false)} />
					)}
				</StyledPaper>
			</Modal>
		</>
	)
}

function RecipientPicker({
	listedEmails,
	hasEligibleEmail,
	selectedEmail,
	onSelect,
	onSubmit,
	onCancel,
}: {
	listedEmails: PickerEmail[]
	hasEligibleEmail: boolean
	selectedEmail: string
	onSelect: (email: string) => void
	onSubmit: () => void
	onCancel: () => void
}) {
	return (
		<Box style={{width: "100%"}}>
			<form
				name="ESIGNConsentRequestForm"
				onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
					e.preventDefault()
					onSubmit()
				}}
			>
				<Grid container direction="column" spacing={3}>
					<Grid size={12}>
						<Grid container direction="column" spacing={2}>
							<Grid size={12}>
								<Typography variant="h5">Request E-Sign consent</Typography>
							</Grid>
							<Grid size={12}>
								<Typography style={{color: "rgba(0, 0, 0, .5)"}}>
									{hasEligibleEmail
										? "Choose which email address receives the request."
										: "This customer has no email address that can receive the request."}
								</Typography>
								{listedEmails.length > 0 && (
									<FormControl>
										<RadioGroup
											name="recipient"
											value={selectedEmail}
											onChange={(event) => onSelect(event.target.value)}
										>
											{listedEmails.map((email) => (
												<FormControlLabel
													key={email.Value}
													value={email.Value}
													disabled={!email.ESIGNEligible}
													control={<Radio color="primary" />}
													label={
														<>
															{email.Value}
															<EmailStatusIcon
																title={email.Status.Description}
																icon={email.Status.Icon}
															/>
														</>
													}
												/>
											))}
										</RadioGroup>
									</FormControl>
								)}
							</Grid>
						</Grid>
					</Grid>
					<Grid size={12}>
						<Button fullWidth type="submit" disabled={!selectedEmail}>
							<Typography variant="body1">Request E-Sign consent</Typography>
						</Button>
					</Grid>
					<Grid size={12}>
						<Button fullWidth variant={"outlined"} onClick={onCancel}>
							<Typography variant="body1">Back</Typography>
						</Button>
					</Grid>
				</Grid>
			</form>
		</Box>
	)
}

function LegacyConfirmation({onConfirm, onCancel}: {onConfirm: () => void; onCancel: () => void}) {
	return (
		<Box style={{width: "100%"}}>
			<Grid container direction="column" spacing={3}>
				<Grid size={12}>
					<Grid
						container
						direction="row"
						spacing={2}
						style={{alignItems: "center", justifyContent: "flex-start"}}
					>
						<Grid size={12}>
							<Typography variant="h5">Are you sure you want to request E-Sign consent?</Typography>
						</Grid>
						<Grid size={12}>
							<Typography style={{color: "rgba(0, 0, 0, .5)"}}>
								An automatic email will be sent to the customer.
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid size={12}>
					<Button fullWidth onClick={onConfirm}>
						<Typography variant="body1">Request E-Sign consent</Typography>
					</Button>
				</Grid>
				<Grid size={12}>
					<Button fullWidth variant={"outlined"} onClick={onCancel}>
						<Typography variant="body1">Back</Typography>
					</Button>
				</Grid>
			</Grid>
		</Box>
	)
}

const StyledPaper = styled(Paper)`
	padding: 20px;
	position: relative;
	width: 400px;
	max-height: 95%;
	outline: none;
	overflow: auto;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	border-radius: 10px;

	@media screen and (max-width: 599px) {
		width: 90%;
	}
`

export {ESIGNConsentRequestModal}
